import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

interface AgentNodeData {
  label: string;
  persona: string;
  capabilities: string[];
  status: 'active' | 'idle' | 'error';
  avatarUrl?: string;
}

const AgentNode: React.FC<NodeProps<AgentNodeData>> = ({ data, isConnectable }) => {
  const getStatusColor = () => {
    switch (data.status) {
      case 'active': return 'bg-green-500';
      case 'idle': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="agent-node bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[180px]">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3"
      />
      
      <div className="flex items-center">
        <div className="mr-3 flex-shrink-0">
          {data.avatarUrl ? (
            <img src={data.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold">
              {data.label.charAt(0)}
            </div>
          )}
        </div>
        
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-medium text-gray-900 truncate">{data.label}</h3>
            <span className={`inline-block w-3 h-3 rounded-full ${getStatusColor()}`}></span>
          </div>
          
          <div className="text-xs text-gray-500">
            {data.persona}
          </div>
        </div>
      </div>
      
      {data.capabilities && data.capabilities.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {data.capabilities.slice(0, 3).map((capability, index) => (
            <span key={index} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
              {capability}
            </span>
          ))}
          {data.capabilities.length > 3 && (
            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
              +{data.capabilities.length - 3}
            </span>
          )}
        </div>
      )}
      
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="w-3 h-3"
      />
    </div>
  );
};

export default memo(AgentNode);
